/**
 * Onboarding overlay anchors.
 *
 * Each anchor describes WHERE on the screen the onboarding card should
 * dock for a given step, plus the size/variant of card that anchor is
 * meant to hold:
 *
 *   • `centerSplash` — large hero card centered in the safe area. Used
 *     for the welcome / gesture / finish beats.
 *   • `topRight*`, `bottomRight*`, `topLeft*`, `bottomLeft*` — compact
 *     coach cards docked into a corner so the canvas / chrome the step
 *     is talking about stays unobstructed.
 *
 * Anchors resolve against `SafeInsets` (provided by
 * `CanvasOnboardingContext`) which carry the rectangle currently free
 * of canvas chrome — explorer pane on the left when open, floating
 * agent chat on the right when open. Compact anchors stay clear of
 * those regions; the splash anchor centers within them.
 *
 * Output is a `position: fixed` rectangle in viewport pixels. The
 * overlay component animates between successive resolved rectangles
 * (gated on `prefers-reduced-motion`).
 */

export type AnchorId =
  | "centerSplash"
  | "topSplash"
  | "centerCompact"
  | "topCenterCompact"
  | "topLeftCompact"
  | "topLeftCompactWide"
  | "topRightCompact"
  | "bottomLeftCompact"
  | "bottomLeftNarrow"
  | "leftCenterNarrow"
  | "bottomRightCompact"
  | "rightCenterCompact";

export type AnchorVariant = "splash" | "compact";

export interface SafeInsets {
  /** Pixels of canvas chrome along each edge that the overlay should avoid. */
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const DEFAULT_SAFE_INSETS: SafeInsets = {
  top: 16,
  right: 16,
  bottom: 16,
  left: 16,
};

export interface Viewport {
  width: number;
  height: number;
}

export interface AnchorRect {
  /**
   * Distance from the viewport top. Mutually exclusive with `bottom`:
   * exactly one is set per rect. Top-anchored rects grow downward;
   * bottom-anchored rects grow upward.
   */
  top?: number;
  /**
   * Distance from the viewport bottom. Used by bottom-corner compact
   * anchors so a short card hugs the bottom edge instead of floating
   * above a worst-case height reservation.
   */
  bottom?: number;
  left: number;
  width: number;
  /**
   * Compact anchors return `"auto"` so the card sizes to its content.
   * Splash anchors return a fixed pixel height for the hero layout.
   */
  height: number | "auto";
}

// Compact corners: fixed width; height is content-driven ("auto").
// Top corners pin the top edge of the rect; bottom corners pin the
// bottom edge of the rect (via `AnchorRect.bottom`), so a short card
// hugs the viewport bottom instead of floating above a height
// reservation.
const COMPACT_W = 520;
const COMPACT_MARGIN = 24;

// Narrow bottom-left variant: 60% of the standard compact width and
// twice the standard edge margin. Used by the "Make it yours" step so
// the card hugs the corner more tightly and leaves more of the canvas
// visible while the user reaches for note / section / shape tools.
const NARROW_W = Math.round(COMPACT_W * 0.6);
const NARROW_MARGIN = COMPACT_MARGIN * 2 + 16;

// Splash steps want a generous, hero-card footprint. ~60% of viewport
// width on a typical 1440-wide screen renders the intrinsic content
// at ~54% scale, which keeps the authored typography readable
// without dominating the canvas behind it.
const SPLASH_W_FRACTION = 0.6;
const SPLASH_MIN_W = 640;
const SPLASH_MAX_W = 1280;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function splashRect(viewport: Viewport, insets: SafeInsets): AnchorRect {
  const safeW = Math.max(0, viewport.width - insets.left - insets.right);
  const targetW = clamp(
    Math.round(viewport.width * SPLASH_W_FRACTION),
    SPLASH_MIN_W,
    SPLASH_MAX_W,
  );
  const width = Math.min(targetW, safeW);
  const left = insets.left + Math.round((safeW - width) / 2);
  // Height is content-driven rather than a rigid 16:9 box: a fixed
  // aspect box clipped the finale content on short viewports. The
  // overlay measures the rendered card and centers it vertically (see
  // `topOverride`), floating it to the top with a margin when the
  // content is taller than the safe area. `top` here is a first-paint
  // fallback only.
  return { top: insets.top, left, width, height: "auto" };
}

// Top-docked splash card. Same horizontal sizing as `centerSplash`
// (hero-card footprint with splash typography), but pinned near the
// top of the safe area with content-driven height. Used by the
// "That's the foundation" finish card so the header sits up top and
// the supporting gesture tiles + finish CTA stack tightly below
// without the card pushing toward the canvas bottom.
const TOP_SPLASH_OFFSET = 24;

function topSplashRect(viewport: Viewport, insets: SafeInsets): AnchorRect {
  const safeW = Math.max(0, viewport.width - insets.left - insets.right);
  const targetW = clamp(
    Math.round(viewport.width * SPLASH_W_FRACTION),
    SPLASH_MIN_W,
    SPLASH_MAX_W,
  );
  const width = Math.min(targetW, safeW);
  const left = insets.left + Math.round((safeW - width) / 2);
  const top = insets.top + TOP_SPLASH_OFFSET;
  return { top, left, width, height: "auto" };
}

function compactRect(
  corner: "tl" | "tr" | "bl" | "br",
  viewport: Viewport,
  insets: SafeInsets,
): AnchorRect {
  // Clamp the compact card so it never extends past the safe area on
  // narrow viewports (mobile splits, side-by-side editors).
  const safeW = Math.max(
    0,
    viewport.width - insets.left - insets.right - COMPACT_MARGIN * 2,
  );
  const width = Math.min(COMPACT_W, safeW);

  const left =
    corner === "tl" || corner === "bl"
      ? insets.left + COMPACT_MARGIN
      : viewport.width - insets.right - COMPACT_MARGIN - width;

  // Top corners pin the rect's top edge — the auto-height card grows
  // downward from there. Bottom corners pin the rect's *bottom* edge
  // so a short card actually hugs the viewport bottom rather than
  // floating above a worst-case height reservation. The overlay
  // applies whichever of `top` / `bottom` is set.
  if (corner === "tl" || corner === "tr") {
    return {
      top: Math.max(insets.top, insets.top + COMPACT_MARGIN),
      left: Math.max(insets.left, left),
      width,
      height: "auto",
    };
  }
  return {
    bottom: Math.max(insets.bottom, insets.bottom + COMPACT_MARGIN),
    left: Math.max(insets.left, left),
    width,
    height: "auto",
  };
}

// Top-left compact card with extra rail clearance — used by steps that
// want more breathing room off the left toolbar. Explicit pixel values
// (rather than a multiple of COMPACT_MARGIN) so the inset can be tuned
// independently of the standard corner geometry.
const WIDE_LEFT_PAD = 56;
const WIDE_TOP_EXTRA = 8;

function topLeftCompactWideRect(
  viewport: Viewport,
  insets: SafeInsets,
): AnchorRect {
  const safeW = Math.max(
    0,
    viewport.width -
      insets.left -
      insets.right -
      WIDE_LEFT_PAD -
      COMPACT_MARGIN,
  );
  const width = Math.min(COMPACT_W, safeW);
  return {
    top: insets.top + COMPACT_MARGIN + WIDE_TOP_EXTRA,
    left: Math.max(insets.left, insets.left + WIDE_LEFT_PAD),
    width,
    height: "auto",
  };
}

function narrowBottomLeftRect(
  viewport: Viewport,
  insets: SafeInsets,
): AnchorRect {
  const safeW = Math.max(
    0,
    viewport.width - insets.left - insets.right - NARROW_MARGIN * 2,
  );
  const width = Math.min(NARROW_W, safeW);
  return {
    bottom: Math.max(insets.bottom, insets.bottom + NARROW_MARGIN),
    left: Math.max(insets.left, insets.left + NARROW_MARGIN),
    width,
    height: "auto",
  };
}

// Vertically centered, left-docked narrow card. Sits just to the right
// of the canvas toolbar (which is pinned to the viewport's left edge,
// vertically centered) so the card and the toolbar share the same
// horizontal axis — the user's eye goes "card → toolbar → canvas"
// without re-locating. Used by the "Make it yours" step where the
// lesson is about reaching for note / section / shape tools in the
// toolbar specifically.
//
// The toolbar is ~42px wide (size-8 button + shell padding); we add a 1rem
// left inset (see CanvasToolbar) plus a generous gap so the card doesn't crowd it.
const LEFT_TOOLBAR_CLEARANCE = 80;
const LEFT_CENTER_GAP = 16;

function leftCenterNarrowRect(
  viewport: Viewport,
  insets: SafeInsets,
): AnchorRect {
  const safeW = Math.max(
    0,
    viewport.width -
      insets.left -
      insets.right -
      LEFT_TOOLBAR_CLEARANCE -
      LEFT_CENTER_GAP,
  );
  const width = Math.min(NARROW_W, safeW);
  const left = insets.left + LEFT_TOOLBAR_CLEARANCE + LEFT_CENTER_GAP;
  // Approximate vertical centering for an auto-height card. The
  // "Make it yours" step renders a heading + lede + a single hint
  // row; ~220px is a close estimate. Centering against an estimate
  // is the same trick `centerCompactRect` uses.
  const safeH = Math.max(0, viewport.height - insets.top - insets.bottom);
  const approxH = 220;
  const top = insets.top + Math.max(0, Math.round((safeH - approxH) / 2));
  return {
    top,
    left: Math.max(insets.left, left),
    width,
    height: "auto",
  };
}

// Right-docked, vertically centered narrow card. Used by DragBriefStep
// (Step 3) so the coachmark sits on the right side of the viewport,
// keeping the canvas drop area open to the left. Same width as the
// narrow bottom-left variant.
//
// The `top` returned here is only a first-paint fallback: the overlay
// measures the rendered card and recomputes `top` so the card is
// centered vertically when it fits and pinned to the top of the safe
// area when it doesn't (short viewports) — instead of overflowing the
// bottom edge and clipping its CTA.
const RIGHT_COMPACT_GAP = 16;

function rightCenterCompactRect(
  viewport: Viewport,
  insets: SafeInsets,
): AnchorRect {
  const safeW = Math.max(
    0,
    viewport.width -
      insets.left -
      insets.right -
      RIGHT_COMPACT_GAP -
      COMPACT_MARGIN,
  );
  const width = Math.min(NARROW_W, safeW);
  const left = viewport.width - insets.right - COMPACT_MARGIN - width;
  return {
    top: insets.top,
    left: Math.max(insets.left, left),
    width,
    height: "auto",
  };
}

// Compact-width card pinned to the top of the safe area and centered
// horizontally. Same width sizing as `centerCompact`; differs only in
// that the card hugs the top edge (with a small breathing margin) so
// it doesn't cover canvas content the user needs to click.
const TOP_CENTER_COMPACT_OFFSET = 24;

function topCenterCompactRect(
  viewport: Viewport,
  insets: SafeInsets,
): AnchorRect {
  const safeW = Math.max(0, viewport.width - insets.left - insets.right);
  const width = Math.min(COMPACT_W, safeW);
  const left = insets.left + Math.round((safeW - width) / 2);
  const top = insets.top + TOP_CENTER_COMPACT_OFFSET;
  return { top, left, width, height: "auto" };
}

function centerCompactRect(viewport: Viewport, insets: SafeInsets): AnchorRect {
  const safeW = Math.max(0, viewport.width - insets.left - insets.right);
  const width = Math.min(COMPACT_W, safeW);
  const left = insets.left + Math.round((safeW - width) / 2);
  // Top is set to roughly vertically center an auto-height card. The
  // overlay anchors by `top`, and since height is content-driven we
  // approximate vertical centering against an expected card height.
  const safeH = Math.max(0, viewport.height - insets.top - insets.bottom);
  const approxH = 260;
  const top = insets.top + Math.max(0, Math.round((safeH - approxH) / 2));
  return { top, left, width, height: "auto" };
}

export function resolveAnchor(
  id: AnchorId,
  viewport: Viewport,
  insets: SafeInsets = DEFAULT_SAFE_INSETS,
): AnchorRect {
  switch (id) {
    case "centerSplash":
      return splashRect(viewport, insets);
    case "topSplash":
      return topSplashRect(viewport, insets);
    case "centerCompact":
      return centerCompactRect(viewport, insets);
    case "topCenterCompact":
      return topCenterCompactRect(viewport, insets);
    case "topLeftCompact":
      return compactRect("tl", viewport, insets);
    case "topLeftCompactWide":
      return topLeftCompactWideRect(viewport, insets);
    case "topRightCompact":
      return compactRect("tr", viewport, insets);
    case "bottomLeftCompact":
      return compactRect("bl", viewport, insets);
    case "bottomLeftNarrow":
      return narrowBottomLeftRect(viewport, insets);
    case "leftCenterNarrow":
      return leftCenterNarrowRect(viewport, insets);
    case "bottomRightCompact":
      return compactRect("br", viewport, insets);
    case "rightCenterCompact":
      return rightCenterCompactRect(viewport, insets);
  }
}

export function variantForAnchor(id: AnchorId): AnchorVariant {
  return id === "centerSplash" || id === "topSplash" ? "splash" : "compact";
}
